import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UnifiedService } from '../unified.service';
import { UntypedFormControl } from '@angular/forms';
import { LocalStorageService } from 'src/app/_services/local-storage.service';
import { Category, CategoryType, IUnifiedIntegration } from '../unified.types';
import { AegisIntegrationService } from 'src/app/_services/aegisIntegration.service';

@Component({
    selector: 'app-integration',
    templateUrl: './integration.component.html',
    styleUrls: ['./integration.component.scss'],
    standalone: false
})
export class IntegrationComponent implements OnInit {

  successMessage: string;

  errorMessage: string;

  /**
   * The category name.
   */
  category: string;

  /**
   * Whether a search is ongoing.
   */
  searchOngoing: boolean = false;

  /**
   * The search form control.
   */
  searchFormControl: UntypedFormControl = new UntypedFormControl('');

  isTeamAdmin: boolean = false;

  integrations: (IUnifiedIntegration & { user: null | { enabled: boolean, connection_id: string }, can_connect: boolean })[] = [];

  isAtsConnected: boolean = false;
  aegisIntegration: any = {
    name: 'Aegis',
    logo: '',
    type: 'aegis',
    enabled: true,
    user: null,
    can_connect: true
  };

  get activeIntegrations() {
    return this.integrations.filter((integration) => integration.user && integration.can_connect && integration.user.enabled);
  }

  get availableIntegrations() {
    return this.integrations.filter((integration) => !integration.user && integration.can_connect);
  }

  get inactiveIntegrations() {
    return this.integrations.filter((integration) => (!integration.user || !integration.user.enabled) && !integration.can_connect);
  }

  constructor(private activatedRoute: ActivatedRoute, private unifiedService: UnifiedService, private aegisIntegrationService: AegisIntegrationService, private router: Router, private localStorageService: LocalStorageService) { }

  ngOnInit() {
    // check if the user is a team admin
    this.isTeamAdmin = !!this.localStorageService.getItem('isteamAdmin');
    this.activatedRoute.fragment.subscribe((fragment) => {
      if (fragment === 'connected') {
        this.toastmessage('Integration connected successfully.', 'success');
      } else if (fragment === 'disconnected') {
        this.toastmessage('Integration disconnected successfully.', 'success');
      } else if (fragment === 'error') {
        this.toastmessage('An error occurred while connecting the integration.', 'error');
      }
      this.router.navigate([], { fragment: undefined, replaceUrl: true });
    });
    this.activatedRoute.params.subscribe(params => {
      // check if the category is valid
      if (!Object.values(Category).includes(params.category)) {
        this.router.navigate(['/settings/ats-integrations', Category.ATS]);
        return;
      }
      this.category = this.readableCategory(params.category);
    });

    const accessCode = this.activatedRoute.snapshot.queryParams['access_code'];
    const state = this.activatedRoute.snapshot.queryParams['state'];

    if (accessCode && state) {
      this.connectAegisIntegration(accessCode, state);
    } else {
      this.checkAegisIntegrationConnected();
    };

    this.searchFormControl.valueChanges.subscribe((value) => {
      this.searchOngoing = value.length > 0;
      this.fetchIntegrations();
    });
    this.fetchIntegrations();
  }

  /**
   * Aegis integration connection handler. 
   * It calls the Aegis integration service to create a new Aegis integration using the provided access code and state. 
   * Based on the response, it shows a success or error message and refreshes the list of integrations.
   * @param accessCode 
   * @param state 
   */
  connectAegisIntegration(accessCode: string, state: string) {
    this.aegisIntegrationService.createAegisIntegration(accessCode, state).subscribe((response: any) => {
      if (response && response.body && response.body?.success) {
        this.toastmessage('Aegis integration connected successfully.', 'success');
        this.checkAegisIntegrationConnected();
      } else {
        this.toastmessage('An error occurred while connecting the Aegis integration.', 'error');
      }
    }, (error) => {
        if (error && error.error && error.error?.message == 'ats_integration_unauthorized') {
          this.toastmessage('Unauthorized User Access', 'error');
        } else {
          this.toastmessage('An error occurred while connecting the Aegis integration.', 'error');
        }
    });
  };

  /**
   * Checks if the Aegis integration is connected by calling the Aegis integration service.
   */
  checkAegisIntegrationConnected() {
    this.aegisIntegrationService.checkAegisIntegrationConnected().subscribe((response: any) => {
      if (response && response.body && response.body?.success) {
        this.isAtsConnected = true;
        this.aegisIntegration['id'] = response.body.data.atsintegrationid;
        this.aegisIntegration['createdAt'] = response.body.data.createdon;
        this.aegisIntegration['updatedAt'] = response.body.data.updatedon;
        this.aegisIntegration['user'] = response.body.data.isactive ? { enabled: true, connection_id: response.body.data.userid } : null;
      } else {
        this.aegisIntegration['id'] = null;
        this.aegisIntegration['createdAt'] = null;
        this.aegisIntegration['updatedAt'] = null;
        this.aegisIntegration['user'] = null;
      }
    }, (error) => {
      this.aegisIntegration['id'] = null;
      this.aegisIntegration['createdAt'] = null;
      this.aegisIntegration['updatedAt'] = null;
      this.aegisIntegration['user'] = null;
    });
  };

  fetchIntegrations() {
    this.unifiedService.getUserUnifiedIntegrations(this.searchFormControl.value).subscribe((response) => {
      if (response.status === 'success') {
        const integrations = response.data || [];
        const hasAegis = integrations.some((integration) => integration.type === this.aegisIntegration.type);
        this.integrations = hasAegis ? integrations : integrations.concat(this.aegisIntegration);
        this.isAtsConnected = this.integrations.some((integration) => integration.user !== null) ? true : false;
      } else {
        this.integrations = [this.aegisIntegration];
      }
    });
  }
  /**
   * Converts the category name to a readable format.
   * @param category Either the category name or the category type.
   * @returns
   */
  private readableCategory(category: CategoryType): string {
    switch (category) {
      case 'ats':
        return 'ATS';
      default:
        return (category as string).replace(/[-_]/g, ' ').split(' ').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }
  }

  /**
   * Flash a toast message.
   * @param message Message to display
   * @param type Type of message
   */
  private toastmessage(message: string, type: 'success' | 'error') {
    if (type == 'success') {
      this.successMessage = message;
      setTimeout(() => this.successMessage = '', 5000);
    } else if (type == 'error') {
      this.errorMessage = message;
      setTimeout(() => this.errorMessage = '', 5000);
    }
  }

  /**
   * Show the integration state message.
   * @param param0
   */
  showIntegrationState({ state, integration }: { state: 'connected' | 'disconnected' | 'already_connected', integration: IUnifiedIntegration }) {
    const titleCase = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);
    if (state === 'connected') {
      this.toastmessage(`${titleCase(integration.name)} connected successfully.`, 'success');
    } else if (state === 'disconnected') {
      this.toastmessage(`${titleCase(integration.name)} disconnected successfully.`, 'success');
    } else if (state === 'already_connected') {
      this.toastmessage('You can only connect one ATS at a time.', 'error');
    }
    this.fetchIntegrations();
  }

}
